/**
 * Feature Toggle Context
 *
 * Provides feature toggle state and evaluation for the application.
 * Fetches feature toggles from backend and caches them locally.
 *
 * Features:
 * - Automatic loading on mount
 * - LocalStorage caching with TTL
 * - Background polling for updates
 * - Works for both authenticated and guest users
 * - Maintenance mode detection
 *
 * @module frontend/src/contexts/FeatureToggleContext
 */

/* eslint-disable react-refresh/only-export-components */

import type React from 'react';
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getAmplifyClient } from '../../lib/amplifyClient';
import { useAuth } from '../../features/auth';

// ========================================
// TYPES
// ========================================

/**
 * Evaluated toggle from backend
 */
export interface EvaluatedToggle {
  toggleId: string;
  enabled: boolean;
  variant?: string; // For A/B testing experiments
}

/**
 * Feature Toggle context state
 */
export interface FeatureToggleContextType {
  /** Map of toggleId -> enabled state */
  toggles: Map<string, EvaluatedToggle>;
  /** Loading state */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Timestamp when toggles were last fetched */
  lastUpdated: Date | null;
  /** Check if a specific toggle is enabled */
  isEnabled: (toggleId: string, defaultValue?: boolean) => boolean;
  /** Get variant for A/B test toggle */
  getVariant: (toggleId: string) => string | undefined;
  /** Force refresh toggles from backend */
  refresh: () => Promise<void>;
  /** Check if maintenance mode is active */
  isMaintenanceMode: boolean;
}

// ========================================
// CONSTANTS
// ========================================

const CACHE_KEY = 'ecompanion2_feature_toggles';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes (match backend Lambda cache)
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes polling

/**
 * LocalStorage cache structure
 */
interface ToggleCache {
  toggles: EvaluatedToggle[];
  timestamp: number;
  userId?: string;
}

// ========================================
// CONTEXT
// ========================================

const FeatureToggleContext = createContext<FeatureToggleContextType | undefined>(undefined);

// ========================================
// CACHE HELPERS
// ========================================

/**
 * Load toggles from localStorage cache
 */
function loadFromCache(userId?: string): EvaluatedToggle[] | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: ToggleCache = JSON.parse(cached);

    // Check if cache is expired
    if (Date.now() - data.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    // Check if cache is for same user (toggles may differ per user)
    if (data.userId !== userId) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return data.toggles;
  } catch {
    return null;
  }
}

/**
 * Save toggles to localStorage cache
 */
function saveToCache(toggles: EvaluatedToggle[], userId?: string): void {
  try {
    const data: ToggleCache = {
      toggles,
      timestamp: Date.now(),
      userId,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors (quota exceeded, private browsing, etc.)
  }
}

// ========================================
// PROVIDER
// ========================================

interface FeatureToggleProviderProps {
  children: React.ReactNode;
}

/**
 * Feature Toggle Provider Component
 *
 * Wraps the application and provides feature toggle state.
 * Fetches toggles on mount and polls for updates.
 */
export function FeatureToggleProvider({ children }: FeatureToggleProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [toggles, setToggles] = useState<Map<string, EvaluatedToggle>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const userId = user?.userId;

  /**
   * Fetch toggles from backend
   */
  const fetchToggles = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      const client = getAmplifyClient();

      // Build input with user context
      const input = {
        userId: userId || undefined,
        // subscriptionTier could be added here if needed
      };

      // Use apiKey auth mode for public access (works for both guest and authenticated)
      const result = await client.queries.getFeatureToggles(
        { input: JSON.stringify(input) },
        { authMode: 'apiKey' }
      );

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const data = result.data as unknown as {
        success: boolean;
        toggles: EvaluatedToggle[];
        error?: string;
      };

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to fetch feature toggles');
      }

      // Convert array to Map for O(1) lookups
      const toggleMap = new Map<string, EvaluatedToggle>();
      for (const toggle of data.toggles || []) {
        toggleMap.set(toggle.toggleId, toggle);
      }

      setToggles(toggleMap);
      setLastUpdated(new Date());

      // Save to cache
      saveToCache(data.toggles || [], userId);

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch feature toggles';
      setError(message);

      // On error, try to use cached data
      const cached = loadFromCache(userId);
      if (cached) {
        const toggleMap = new Map<string, EvaluatedToggle>();
        for (const toggle of cached) {
          toggleMap.set(toggle.toggleId, toggle);
        }
        setToggles(toggleMap);
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /**
   * Load toggles on mount and when user changes
   */
  useEffect(() => {
    // Try to load from cache first for instant rendering
    const cached = loadFromCache(userId);
    if (cached) {
      const toggleMap = new Map<string, EvaluatedToggle>();
      for (const toggle of cached) {
        toggleMap.set(toggle.toggleId, toggle);
      }
      setToggles(toggleMap);
      setLoading(false);
    }

    // Then fetch fresh data
    void fetchToggles(!cached);
  }, [fetchToggles, userId]);

  /**
   * Set up polling for updates
   */
  useEffect(() => {
    const interval = setInterval(() => {
      void fetchToggles(false); // Silent refresh (no loading state)
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchToggles]);

  /**
   * Refresh toggles when auth state changes
   */
  useEffect(() => {
    // Clear cache and refresh when user logs in/out
    localStorage.removeItem(CACHE_KEY);
    void fetchToggles(true);
  }, [isAuthenticated, fetchToggles]);

  /**
   * Check if a toggle is enabled
   */
  const isEnabled = useCallback(
    (toggleId: string, defaultValue = false): boolean => {
      const toggle = toggles.get(toggleId);
      if (!toggle) return defaultValue;
      return toggle.enabled;
    },
    [toggles]
  );

  /**
   * Get variant for A/B test toggle
   */
  const getVariant = useCallback(
    (toggleId: string): string | undefined => {
      const toggle = toggles.get(toggleId);
      return toggle?.variant;
    },
    [toggles]
  );

  /**
   * Force refresh toggles
   */
  const refresh = useCallback(async () => {
    localStorage.removeItem(CACHE_KEY);
    await fetchToggles(true);
  }, [fetchToggles]);

  /**
   * Check if maintenance mode is active
   */
  const isMaintenanceMode = useMemo(() => {
    const toggle = toggles.get('maintenance_mode');
    return toggle?.enabled ?? false;
  }, [toggles]);

  const value: FeatureToggleContextType = {
    toggles,
    loading,
    error,
    lastUpdated,
    isEnabled,
    getVariant,
    refresh,
    isMaintenanceMode,
  };

  return (
    <FeatureToggleContext.Provider value={value}>
      {children}
    </FeatureToggleContext.Provider>
  );
}

// ========================================
// HOOKS
// ========================================

/**
 * Hook to use feature toggle context
 *
 * @throws Error if used outside of FeatureToggleProvider
 */
export function useFeatureToggles(): FeatureToggleContextType {
  const context = useContext(FeatureToggleContext);

  if (context === undefined) {
    throw new Error('useFeatureToggles must be used within a FeatureToggleProvider');
  }

  return context;
}

/**
 * Hook to check a single feature toggle
 *
 * @param toggleId - The toggle ID to check
 * @param defaultValue - Default value if toggle not found (default: false)
 * @returns Whether the toggle is enabled
 *
 * @example
 * ```typescript
 * const isProCalculatorEnabled = useFeatureToggle('pro_calculator_enabled');
 *
 * if (isProCalculatorEnabled) {
 *   // Show professional calculator
 * }
 * ```
 */
export function useFeatureToggle(toggleId: string, defaultValue = false): boolean {
  const { isEnabled } = useFeatureToggles();
  return isEnabled(toggleId, defaultValue);
}

/**
 * Hook to get A/B test variant
 *
 * @param toggleId - The experiment toggle ID
 * @returns The variant name or undefined if not in experiment
 *
 * @example
 * ```typescript
 * const variant = useExperimentVariant('new_results_ui');
 *
 * if (variant === 'variant_b') {
 *   // Show new results UI
 * }
 * ```
 */
export function useExperimentVariant(toggleId: string): string | undefined {
  const { getVariant, isEnabled } = useFeatureToggles();

  // Only return variant if toggle is enabled
  if (!isEnabled(toggleId, false)) {
    return undefined;
  }

  return getVariant(toggleId);
}

/**
 * Hook to check maintenance mode
 *
 * @returns Whether maintenance mode is active
 *
 * @example
 * ```typescript
 * const isMaintenanceMode = useMaintenanceMode();
 *
 * if (isMaintenanceMode) {
 *   return <MaintenancePage />;
 * }
 * ```
 */
export function useMaintenanceMode(): boolean {
  const { isMaintenanceMode } = useFeatureToggles();
  return isMaintenanceMode;
}
